import { Text, Box } from '@radix-ui/themes'
import { IconInfoCircle, IconLoader } from '@tabler/icons-react'
import { getMarksStyle } from '@/utils/style'
import Hstack from '@/components/ui/hstack'
import Stack from '@/components/ui/stack'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip'
import { TutorMarking, AiMarking } from '@/components/svg/total-student'

export interface MarkingStatusProps {
  aiMarks?: number
  aiStatus?: string
  tutorMarks?: number
  tutorStatus?: string
  totalMarks?: number
  showTutorMarks?: boolean
}

export default function MarkingStatus({
  aiMarks,
  aiStatus,
  tutorMarks,
  tutorStatus,
  totalMarks,
  showTutorMarks = true,
}: MarkingStatusProps) {
  return (
    <Hstack gap={'12px'} className='mb-4 w-full'>
      <Box
        height={'fit-full'}
        p={'20px'}
        className={`w-full rounded-md border bg-white`}
        // ${getMarksStyle(aiMarks, totalMarks, true)}
      >
        <Hstack justify='between' align='start'>
          <Hstack gap={'12px'} align='start'>
            <Box className='flex items-center justify-center rounded-full bg-blue-100'>
              <AiMarking size={48} />
            </Box>
            <Stack gap={'8px'}>
              <Hstack gap={'8px'} align='start'>
                <Text className='text-sm font-medium text-gray-700'>
                  AI Marks
                </Text>
              </Hstack>
              <Box className={`w-fit rounded-md border px-2 py-0.5`}>
                <Hstack gap={'6px'} align='center'>
                  {!aiStatus && <IconLoader size={12} color='#0069E0' />}
                  <Text className={`text-[16px] font-semibold text-gray-600`}>
                    {aiStatus || 'Unmarked'}
                  </Text>
                </Hstack>
              </Box>
            </Stack>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type='button'
                  aria-label='Assessment info'
                  className='rounded-full p-1 text-gray-400 hover:text-gray-600 focus:ring-1 focus:ring-blue-500 focus:ring-offset-2 focus:outline-hidden'
                  tabIndex={0}
                >
                  <IconInfoCircle size={24} color='black' />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side='right'
                sideOffset={8}
                hideArrow
                className='max-w-[340px] rounded-[20px] bg-[#D9D9D9] px-4 py-3 text-[12px] text-[#404040] shadow-none'
              >
                <Stack gap={'0px'}>
                  <Text>
                    This result was graded by AI and may not fully reflect the
                    accurate outcome. Please consider it as a reference only.
                  </Text>
                </Stack>
              </TooltipContent>
            </Tooltip>
          </Hstack>
          <Hstack justify={'between'} gap={'4px'}>
            {aiMarks != null ? (
              <>
                <Text
                  className={`${getMarksStyle(aiMarks, totalMarks, false)} text-[60px] font-bold text-gray-900`}
                >
                  {aiMarks}
                </Text>
                <Text className='text-[60px] font-light text-gray-500'>
                  /{totalMarks}
                </Text>
              </>
            ) : (
              <Text className='text-4xl font-bold text-[#002652]'>--</Text>
            )}
            <div className='mt-3 ml-2 align-top'>
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <IconInfoCircle className='min-h-6 min-w-6' />
                  </TooltipTrigger>
                  <TooltipContent
                    hideArrow
                    className='max-w-[360px] bg-[#D9D9D9] text-xs text-[#000000]'
                  >
                    <Box className='w-full text-left text-[12px] text-wrap text-black'>
                      <p>The colors show how well you performed.</p>
                      <ul className='list-disc space-y-1 pl-6'>
                        <li className='leading-4'>
                          <span className='font-bold text-[#D92D20]'>Red</span>{' '}
                          means your score is
                          <span className='font-bold text-[#D92D20]'>
                            {' '}
                            below 45%
                          </span>
                          , which is a{' '}
                          <span className='font-bold text-[#D92D20]'>Fail</span>
                          . You should review the material carefully.
                        </li>
                        <li className='leading-4'>
                          <span className='font-bold text-[#F79009]'>
                            Amber
                          </span>{' '}
                          (yellow) means your score is{' '}
                          <span className='font-bold text-[#F79009]'>
                            between 45% and 52%
                          </span>
                          , which is a{' '}
                          <span className='font-bold text-[#F79009]'>
                            Borderline Pass
                          </span>
                          . You still need some improvement.
                        </li>
                        <li className='leading-4'>
                          <span className='text-[#17B26A]'>Green</span> means
                          your score is
                          <span className='font-bold text-[#17B26A]'>
                            {' '}
                            above 52%
                          </span>
                          , which is a{' '}
                          <span className='font-bold text-[#17B26A]'>
                            Safe Pass
                          </span>
                          . You have passed and demonstrated good understanding.
                        </li>
                      </ul>
                    </Box>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </Hstack>
        </Hstack>
      </Box>

      <Box
        height={'fit-full'}
        p={'20px'}
        className={`w-full rounded-md border bg-white`}
        // ${getMarksStyle(tutorMarks, totalMarks, true)}
      >
        <Hstack justify='between' align='start'>
          <Hstack gap={'12px'} align='start'>
            <Box className='flex items-center justify-center rounded-full bg-blue-100'>
              <TutorMarking size={48} />
            </Box>
            <Stack gap={'8px'}>
              <Text className='text-sm font-medium text-gray-700'>
                Tutor Marks
              </Text>

              <Box className={`w-fit rounded-md border px-2 py-0.5`}>
                <Hstack gap={'6px'} align='center'>
                  {!tutorStatus && <IconLoader size={12} color='#0069E0' />}
                  <Text className={`text-[16px] font-semibold text-gray-600`}>
                    {tutorStatus || 'Unmarked'}
                  </Text>
                </Hstack>
              </Box>
            </Stack>
          </Hstack>
          <Hstack gap={'4px'}>
            {showTutorMarks && tutorMarks != null ? (
              <>
                <Text
                  className={`${getMarksStyle(tutorMarks, totalMarks, false)} text-[60px] font-bold text-gray-900`}
                >
                  {tutorMarks}
                </Text>
                <Text className='text-[60px] font-light text-gray-500'>
                  /{totalMarks}
                </Text>
              </>
            ) : (
              <Text className='text-4xl font-bold text-[#002652]'>--</Text>
            )}
            <div className='mt-3 ml-2'>
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <IconInfoCircle className='min-h-6 min-w-6' />
                  </TooltipTrigger>
                  <TooltipContent
                    hideArrow
                    className='max-w-[360px] bg-[#D9D9D9] text-xs text-[#000000]'
                  >
                    <Box className='w-full text-left text-[12px] text-wrap text-black'>
                      <p>The colors show how well you performed.</p>
                      <ul className='list-disc space-y-1 pl-6'>
                        <li className='leading-4'>
                          <span className='font-bold text-[#D92D20]'>Red</span>{' '}
                          means your score is
                          <span className='font-bold text-[#D92D20]'>
                            {' '}
                            below 45%
                          </span>
                          , which is a{' '}
                          <span className='font-bold text-[#D92D20]'>Fail</span>
                          . You should review the material carefully.
                        </li>
                        <li className='leading-4'>
                          <span className='font-bold text-[#F79009]'>
                            Amber
                          </span>{' '}
                          (yellow) means your score is{' '}
                          <span className='font-bold text-[#F79009]'>
                            between 45% and 52%
                          </span>
                          , which is a{' '}
                          <span className='font-bold text-[#F79009]'>
                            Borderline Pass
                          </span>
                          . You still need some improvement.
                        </li>
                        <li className='leading-4'>
                          <span className='text-[#17B26A]'>Green</span> means
                          your score is
                          <span className='font-bold text-[#17B26A]'>
                            {' '}
                            above 52%
                          </span>
                          , which is a{' '}
                          <span className='font-bold text-[#17B26A]'>
                            Safe Pass
                          </span>
                          . You have passed and demonstrated good understanding.
                        </li>
                      </ul>
                    </Box>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </Hstack>
        </Hstack>
      </Box>
    </Hstack>
  )
}
